import React, { useState, useEffect, useCallback } from 'react';
import {
  FiSearch,
  FiMoreVertical,
  FiUserCheck,
  FiUserX,
  FiTrash2,
  FiUsers,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle
} from 'react-icons/fi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useNotification } from '../../contexts/NotificationContext';
import { adminAPI } from '../../services/api';

const TeachersManagement = () => {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, suspended: 0 });
  const { showError, showSuccess } = useNotification();

  const fetchTeachers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAllUsers({ role: 'teacher' });
      const raw = response?.data?.data || [];
      const normalized = raw.map((u) => ({
        id: u._id || u.id,
        _id: u._id || u.id,
        name: u.fullName || `${u.firstName || ''} ${u.lastName || ''}`.trim(),
        email: u.email || '',
        avatar: u.avatar || '',
        createdAt: u.createdAt,
        status: u.status || (u.isActive === false ? 'inactive' : 'active'),
        sessionsCount: u.sessionsCount || 0,
        rating: u.rating
      }));
      setTeachers(normalized);
    } catch (error) {
      showError('Error fetching teachers');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await adminAPI.getUsersStats({ role: 'teacher' });
      const data = response?.data?.data || {};
      setStats({ total: data.total || 0, active: data.active || 0, inactive: data.inactive || 0, suspended: data.suspended || 0 });
    } catch (_) {}
  }, []);

  useEffect(() => {
    fetchTeachers();
    fetchStats();
  }, [fetchTeachers, fetchStats]);

  const handleStatusChange = async (teacherId, newStatus) => {
    try {
      await adminAPI.updateUserStatus(teacherId, newStatus);
      showSuccess('Teacher status updated');
      fetchTeachers();
      fetchStats();
      setMenuOpen(null);
    } catch (error) {
      showError('Failed to update teacher status');
    }
  };

  const handleDeleteTeacher = async (teacherId) => {
    if (!window.confirm('Are you sure you want to delete this teacher?')) return;
    try {
      await adminAPI.deleteUser(teacherId);
      showSuccess('Teacher deleted');
      fetchTeachers();
      fetchStats();
      setMenuOpen(null);
    } catch (error) {
      showError('Failed to delete teacher');
    }
  };

  const filteredTeachers = teachers.filter((t) => {
    const matchSearch = (t.name || '').toLowerCase().includes(searchTerm.toLowerCase()) || (t.email || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === 'all' || t.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const statusBadge = (status) => {
    const map = { active: 'bg-emerald-100 text-emerald-800', inactive: 'bg-slate-100 text-slate-700', suspended: 'bg-red-100 text-red-800' };
    return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${map[status] || 'bg-slate-100 text-slate-700'}`}>{status}</span>;
  };

  if (loading && teachers.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50/60 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/60">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-slate-900 tracking-tight">Teachers</h1>
          <p className="text-sm text-slate-500 mt-0.5">Manage teacher accounts and status.</p>
        </div>

        <div className="flex flex-wrap items-center gap-6 py-3 px-4 rounded-lg bg-white border border-slate-200/80 shadow-sm mb-6">
          <div className="flex items-center gap-2">
            <FiUsers className="h-4 w-4 text-slate-400" />
            <span className="text-sm text-slate-600">Total</span>
            <span className="text-sm font-semibold text-slate-900">{stats.total}</span>
          </div>
          <div className="h-4 w-px bg-slate-200" />
          <div className="flex items-center gap-2">
            <FiCheckCircle className="h-4 w-4 text-emerald-500" />
            <span className="text-sm text-slate-600">Active</span>
            <span className="text-sm font-semibold text-slate-900">{stats.active}</span>
          </div>
          <div className="h-4 w-px bg-slate-200" />
          <div className="flex items-center gap-2">
            <FiXCircle className="h-4 w-4 text-slate-400" />
            <span className="text-sm text-slate-600">Inactive</span>
            <span className="text-sm font-semibold text-slate-900">{stats.inactive}</span>
          </div>
          <div className="h-4 w-px bg-slate-200" />
          <div className="flex items-center gap-2">
            <FiAlertCircle className="h-4 w-4 text-red-500" />
            <span className="text-sm text-slate-600">Suspended</span>
            <span className="text-sm font-semibold text-slate-900">{stats.suspended}</span>
          </div>
        </div>

        <div className="mb-6 flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="relative flex-1 max-w-sm">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search teachers..." className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400 placeholder:text-slate-400" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400">
            <option value="all">All status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </select>
          {filteredTeachers.length > 0 && <span className="text-xs text-slate-500 sm:ml-auto">{filteredTeachers.length} of {teachers.length}</span>}
        </div>

        <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-16"><LoadingSpinner /></div>
          ) : filteredTeachers.length === 0 ? (
            <div className="text-center py-14 px-6">
              <FiUsers className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No teachers match your criteria.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/80">
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Teacher</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Email</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Status</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Joined</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Sessions</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Rating</th>
                    <th className="px-4 py-3 w-0" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredTeachers.map((teacher) => (
                    <tr key={teacher.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-full bg-slate-200 flex items-center justify-center text-sm font-medium text-slate-700 shrink-0 overflow-hidden">
                            {teacher.avatar ? <img src={teacher.avatar} alt="" className="h-full w-full object-cover" /> : (teacher.name && teacher.name.charAt(0).toUpperCase())}
                          </div>
                          <div>
                            <div className="font-medium text-slate-900">{teacher.name}</div>
                            <div className="text-xs text-slate-500">ID: {teacher.id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{teacher.email}</td>
                      <td className="px-4 py-3">{statusBadge(teacher.status)}</td>
                      <td className="px-4 py-3 text-slate-600">{teacher.createdAt ? new Date(teacher.createdAt).toLocaleDateString() : '—'}</td>
                      <td className="px-4 py-3 text-slate-600">{teacher.sessionsCount ?? 0}</td>
                      <td className="px-4 py-3 text-slate-600">{teacher.rating ? `${Number(teacher.rating).toFixed(1)}/5` : 'N/A'}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="relative inline-block">
                          <button type="button" onClick={() => setMenuOpen(menuOpen === teacher.id ? null : teacher.id)} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"><FiMoreVertical className="h-4 w-4" /></button>
                          {menuOpen === teacher.id && (
                            <div className="absolute right-0 top-full mt-1 py-1 w-36 rounded-lg border border-slate-200 bg-white shadow-lg z-10">
                              <button type="button" onClick={() => handleStatusChange(teacher._id, teacher.status === 'active' ? 'inactive' : 'active')} className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2">{teacher.status === 'active' ? <FiUserX className="h-4 w-4" /> : <FiUserCheck className="h-4 w-4" />}{teacher.status === 'active' ? 'Deactivate' : 'Activate'}</button>
                              <button type="button" onClick={() => handleStatusChange(teacher._id, 'suspended')} disabled={teacher.status === 'suspended'} className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2 disabled:opacity-50">Suspend</button>
                              <button type="button" onClick={() => handleDeleteTeacher(teacher._id)} className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"><FiTrash2 className="h-4 w-4" /> Delete</button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeachersManagement;
